import { useApolloClient } from '@apollo/client'
import { GraphQLClient } from 'graphql-request'

import { useCompanyStorage } from 'src/hooks/useCompanyStorage'
import { useError } from 'src/hooks/useError'
import { useProfileStorage } from 'src/hooks/useProfileStorage'
import { isServer } from 'src/libs/helper'
import {
  AuthRepository,
  CommonRepository,
  SaveAccessTokenInput,
  SaveRefreshTokenInput,
  SaveUsernameInput,
} from 'src/repositories'
import { AuthTranslator } from 'src/translators'
import {
  IdPasswordSignInFormEntity,
  IdPassSignInResponse,
  UpdateInitialPasswordFormEntity,
  PasswordForgetFormEntity,
  PasswordForceUpdateFormEntity,
  UpdatePasswordFormEntity,
  SamlSignInResponse,
} from 'src/types'
import {
  useIdPassLoginMutation,
  useUpdateInitialPasswordMutation,
  useUpdatePasswordMutation,
  useForgetPasswordEmailMutation,
  useForceUpdateUserPasswordMutation,
  useForceUpdateExternalUserPasswordMutation,
  useExchangeAuthCodeWithAccessTokenMutation,
} from 'src/types/generated'
import { getSdk } from 'src/types/generated-request'

type IdPassLoginInput = {
  formEntity: IdPasswordSignInFormEntity
}
type SaveRequiredAuthenticationInformationInput = SaveAccessTokenInput &
  SaveRefreshTokenInput &
  SaveUsernameInput
type UpdateInitialPasswordInput = {
  formEntity: UpdateInitialPasswordFormEntity
}
type UpdatePasswordInput = {
  formEntity: UpdatePasswordFormEntity
}
type SendForgetPasswordEmailInput = {
  formEntity: PasswordForgetFormEntity
}
type SendForceUpdatePasswordEmailInput = {
  userId: number
  formEntity: PasswordForceUpdateFormEntity
}

export const useAuth = () => {
  const [idPassLoginMutation] = useIdPassLoginMutation()
  const [updateInitialPasswordMutation] = useUpdateInitialPasswordMutation()
  const [updatePasswordMutation] = useUpdatePasswordMutation()
  const [forgetPasswordEmailMutation] = useForgetPasswordEmailMutation()
  const [forceUpdateUserPasswordMutation] = useForceUpdateUserPasswordMutation()
  const [forceUpdateExternalUserPasswordMutation] = useForceUpdateExternalUserPasswordMutation()
  const [exchangeAuthCodeWithAccessTokenMutation] = useExchangeAuthCodeWithAccessTokenMutation()
  const { removeProfileFromLocalStorage } = useProfileStorage()
  const { removeCompanyFromLocalStorage } = useCompanyStorage()

  const { toErrorEntities } = useError()

  const client = useApolloClient()

  const idPassLogin = async ({ formEntity }: IdPassLoginInput): Promise<IdPassSignInResponse> => {
    const input = AuthTranslator.toIdPassSignInRequest(formEntity)
    const response = await idPassLoginMutation({ variables: { input } })

    const data = response.data?.login?.session
    if (!data) {
      throw new Error('Data required for login does not exist.')
    }

    return {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      accessTokenExpiresAt: data.accessTokenExpiresAt,
      refreshTokenExpiresAt: data.refreshTokenExpiresAt,
      cognitoUsername: data.cognitoUsername,
    }
  }

  const saveRequiredAuthenticationInformation = async (
    input: SaveRequiredAuthenticationInformationInput
  ): Promise<void> => {
    const {
      accessToken,
      accessTokenExpiresAt,
      refreshToken,
      refreshTokenExpiresAt,
      username,
      usernameExpiresAt,
    } = input

    AuthRepository.saveAccessToken({ accessToken, accessTokenExpiresAt })
    await AuthRepository.saveRefreshToken({ refreshToken, refreshTokenExpiresAt })
    await AuthRepository.saveUsername({ username, usernameExpiresAt })
  }

  const samlLogin = async (authCode: string): Promise<SamlSignInResponse> => {
    const response = await exchangeAuthCodeWithAccessTokenMutation({
      variables: { code: authCode },
    })

    const data = response.data?.exchangeAuthCodeWithAccessToken?.session
    if (!data) {
      throw new Error('Data required for login does not exist.')
    }

    return {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      accessTokenExpiresAt: data.accessTokenExpiresAt,
      refreshTokenExpiresAt: data.refreshTokenExpiresAt,
      cognitoUsername: data.cognitoUsername,
    }
  }

  const loadAccessToken = (): string | null => {
    return AuthRepository.loadAccessToken()
  }

  const updateInitialPassword = async ({
    formEntity,
  }: UpdateInitialPasswordInput): Promise<void> => {
    const input = AuthTranslator.toUpdateInitialPasswordRequest(formEntity)
    await updateInitialPasswordMutation({ variables: { input } })
  }

  const updatePassword = async ({ formEntity }: UpdatePasswordInput): Promise<void> => {
    const input = AuthTranslator.toUpdatePasswordRequest(formEntity)
    await updatePasswordMutation({ variables: { input } })
  }

  const sendForgetPasswordEmail = async ({
    formEntity,
  }: SendForgetPasswordEmailInput): Promise<void> => {
    const input = AuthTranslator.toForgetPasswordEmailRequest(formEntity)
    await forgetPasswordEmailMutation({ variables: { input } })
  }

  const removeAllCookie = async (): Promise<void> => {
    await CommonRepository.removeAllCookies()
  }

  const logout = async (): Promise<void> => {
    const refreshToken = await AuthRepository.loadRefreshToken()
    if (refreshToken) {
      await removeAllCookie()

      try {
        // react-apolloのカスタムフックを利用すると bearer-token が自動で付与されるため、graphql-requestを利用してログアウトAPIを呼び出す
        // TODO: react-apolloを使っても bearer-token を付与しない方法があると思うので調査
        await getSdk(new GraphQLClient(process.env.GraphQLUrl ?? '')).logout({
          input: { refreshToken },
        })
      } catch (error) {
        // ログアウトAPIがエラーを返してきた場合はサーバー側でリフレッシュトークンが削除されているはずのため、エラーが起きても特に何もしない
        const errors = toErrorEntities(error)
        console.log('errors: ', errors)
      }
    }

    // Apollo Clientのキャッシュをクリア
    await client.clearStore()

    // GA用に使用するローカルストレージのログイン情報を削除
    if (!isServer) {
      removeProfileFromLocalStorage()
      removeCompanyFromLocalStorage()
    }
  }

  const sendForceUpdateUserPasswordEmail = async ({
    userId,
    formEntity,
  }: SendForceUpdatePasswordEmailInput): Promise<void> => {
    const input = AuthTranslator.toForceUpdatePasswordEmailRequest(formEntity)
    await forceUpdateUserPasswordMutation({ variables: { userId, input } })
  }

  // forceUpdateUserPasswordMutation と forceUpdateExternalUserPasswordMutation は
  // 同じインターフェイスだがバックエンドの処理が異なるため、別々の関数として定義する
  const sendForceUpdateExternalUserPasswordEmail = async ({
    userId,
    formEntity,
  }: SendForceUpdatePasswordEmailInput): Promise<void> => {
    const input = AuthTranslator.toForceUpdatePasswordEmailRequest(formEntity)
    await forceUpdateExternalUserPasswordMutation({ variables: { userId, input } })
  }

  return {
    idPassLogin,
    samlLogin,
    saveRequiredAuthenticationInformation,
    loadAccessToken,
    updateInitialPassword,
    updatePassword,
    sendForgetPasswordEmail,
    sendForceUpdateUserPasswordEmail,
    sendForceUpdateExternalUserPasswordEmail,
    removeAllCookie,
    logout,
  }
}
